/**
 * 预约。
 *
 * 两种来源，一个抽屉都要能处理：
 *   A. 电话进来的陌生人 —— 只有姓名、电话、来做什么，还没建档
 *   B. 老顾客 / 做到一半的工单 —— 从工单详情点「约下次」进来，顾客已知
 *
 * 第一版只做了 B，A 完全没有入口 —— 日历上点「+ 预约」压根填不了姓名电话，
 * 后端会直接 422。这是真 bug，不是功能缺失。
 *
 * 「先建档再预约」是错的顺序：接电话的三十秒里没时间问出生年、地址、语言偏好。
 * 所以陌生人直接存成临时预约（walkin），等他真到店了再一键补建档并开单。
 *
 * 传 appointment 就是编辑模式：改期、改目的、改状态、删除都在这里。
 * 顾客打电话说改时间或者不来了，这是最常发生的事，不能只能新建不能改。
 */

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Search, Trash2, UserPlus, X } from 'lucide-react'
import { api, type Me, type SearchItem } from '../lib/api'
import {
  Avatar,
  Badge,
  Button,
  Drawer,
  Field,
  Input,
  Segmented,
  Textarea,
  cx,
  useToast,
} from '../components/ui'
import { APPT_STATUS_ZH, addDays, dateKey, date as fmtDate, weekday } from '../lib/format'

/** 来电最常说的几件事，点一下比打字快。 */
const PURPOSES = ['咨询做假牙', '取模', '试戴', '取件', '假牙不舒服', '维修', '洗牙咨询']

interface Picked {
  id: string | null
  name: string
  phone: string
}

export interface EditableAppointment {
  id: string
  startsAt: string
  durationMin: number
  status: string
  purpose: string | null
  notes: string | null
  displayName: string
  phoneE164: string | null
  customer: { id: string; fullName: string } | null
}

export function AppointmentDrawer({
  open,
  onClose,
  customerId,
  customerName,
  caseId,
  caseStepId,
  suggestedDate,
  headline,
  appointment,
  isOwner,
}: {
  open: boolean
  onClose: () => void
  customerId?: string
  customerName?: string
  caseId?: string
  caseStepId?: string
  suggestedDate?: string
  headline?: string
  /** 传了就是编辑模式 */
  appointment?: EditableAppointment | null
  isOwner?: boolean
}) {
  const qc = useQueryClient()
  // 与 App.tsx 同一个 queryKey，直接命中缓存，不会多发请求
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/api/auth/me'),
    staleTime: 5 * 60_000,
  })
  const toast = useToast()

  const [picked, setPicked] = useState<Picked | null>(null)
  const [purpose, setPurpose] = useState('')
  const [day, setDay] = useState('')
  const [hhmm, setHhmm] = useState('10:00')
  const [duration, setDuration] = useState(30)
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  const editing = Boolean(appointment)
  /** 从工单点进来、或编辑已有预约：顾客已知，不用再选 */
  const locked = Boolean(customerId) || editing

  useEffect(() => {
    if (!open) return
    if (appointment) {
      const d = new Date(appointment.startsAt)
      // UTC → 吉隆坡本地墙上时间
      const local = new Date(d.getTime() + 8 * 3600_000)
      setPicked({
        id: appointment.customer?.id ?? null,
        name: appointment.displayName,
        phone: appointment.phoneE164 ?? '',
      })
      setPurpose(appointment.purpose ?? '')
      setDay(dateKey(d))
      setHhmm(
        String(local.getUTCHours()).padStart(2, '0') +
          ':' +
          String(local.getUTCMinutes()).padStart(2, '0'),
      )
      setDuration(appointment.durationMin)
      setNotes(appointment.notes ?? '')
    } else {
      setPicked(customerId ? { id: customerId, name: customerName ?? '', phone: '' } : null)
      setPurpose('')
      setDay(suggestedDate ?? dateKey(addDays(new Date(), 7)))
      setHhmm('10:00')
      setDuration(30)
      setNotes('')
    }
    setError(null)
  }, [open, customerId, customerName, suggestedDate, appointment])

  const save = useMutation({
    mutationFn: () => {
      if (!picked || (!picked.id && !picked.name.trim())) {
        throw new Error('请先选顾客，或填写姓名')
      }
      const [h, m] = hhmm.split(':').map(Number)
      const [y, mo, d] = day.split('-').map(Number)
      // 本地墙上时间 → UTC（吉隆坡 +8）
      const startsAt = new Date(Date.UTC(y!, mo! - 1, d!, (h ?? 10) - 8, m ?? 0)).toISOString()

      if (appointment) {
        // 编辑只改时间/目的/备注 —— 换人应该删掉重建，而不是把一条记录的主体偷偷换掉
        return api.patch<{ warnings: Array<{ code: string; message: string }> }>(
          `/api/appointments/${appointment.id}`,
          {
            startsAt,
            durationMin: duration,
            purpose: purpose.trim() || null,
            notes: notes.trim() || null,
          },
        )
      }
      return api.post<{ warnings: Array<{ code: string; message: string }> }>('/api/appointments', {
        customerId: picked.id,
        // 未建档的临时预约：姓名电话直接存在预约上
        walkinName: picked.id ? null : picked.name.trim(),
        walkinPhone: picked.id ? null : picked.phone.trim() || null,
        caseId: caseId ?? null,
        caseStepId: caseStepId ?? null,
        startsAt,
        durationMin: duration,
        purpose: purpose.trim() || null,
        notes: notes.trim() || null,
      })
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['today'] })
      void qc.invalidateQueries({ queryKey: ['appointments'] })
      const w = r.warnings.length > 0 ? '（有提示，见日历）' : ''
      toast.show(`${editing ? '已改到' : '已约'} ${fmtDate(day)} ${hhmm}${w}`)
      onClose()
    },
    onError: (e) => setError((e as Error).message),
  })

  const setStatus = useMutation({
    mutationFn: (status: string) =>
      api.post(`/api/appointments/${appointment!.id}/status`, { status }),
    onSuccess: (_r, status) => {
      void qc.invalidateQueries({ queryKey: ['today'] })
      void qc.invalidateQueries({ queryKey: ['appointments'] })
      toast.show(status === 'NO_SHOW' ? '已记为爽约' : status === 'CANCELLED' ? '已取消' : '已更新')
      onClose()
    },
    onError: (e) => setError((e as Error).message),
  })

  const remove = useMutation({
    mutationFn: () => api.del(`/api/appointments/${appointment!.id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['today'] })
      void qc.invalidateQueries({ queryKey: ['appointments'] })
      toast.show('预约已删除')
      onClose()
    },
    onError: (e) => setError((e as Error).message),
  })

  // 本地先判休息日，让老板在点保存前就看到，而不是保存后才被告知。
  // 【读设置，不写死】—— 原本写的是 dow === 5 || dow === 6，
  // 而店里后来改成只休周五、周六照常营业，于是周六排预约会被误报休息。
  // 营业日只有一个来源：设置里的营业时间。
  const dow = day ? new Date(`${day}T00:00:00Z`).getUTCDay() : -1
  const restDay = dow >= 0 && (me.data?.shop.businessHours?.[String(dow)] ?? null) === null

  const canSave = Boolean(picked && (picked.id || picked.name.trim()) && day && hhmm)

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? '预约详情' : locked ? '约下次' : '新预约'}
      footer={
        <>
          <Button size="lg" onClick={onClose}>
            取消
          </Button>
          <Button
            size="lg"
            variant="primary"
            disabled={!canSave}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            确认预约
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {error && (
          <div className="rounded-md border border-bad-line bg-bad-bg px-4 py-3 text-[16px] text-bad">
            {error}
          </div>
        )}

        {headline && (
          <div className="rounded-md bg-sunken px-4 py-3">
            <p className="text-[16px] text-ink-2">{headline}</p>
          </div>
        )}

        {/* ── 谁 ── */}
        {locked ? (
          <div className="flex items-center gap-3 rounded-md border-2 border-brand-600 bg-brand-50 px-4 py-3.5">
            <Avatar name={appointment?.displayName ?? customerName ?? ''} size={40} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[19px] font-semibold">
                {appointment?.displayName ?? customerName}
              </span>
              {appointment?.phoneE164 && (
                <a
                  href={`tel:${appointment.phoneE164}`}
                  className="tnum block text-[15px] font-medium text-brand-700"
                >
                  {appointment.phoneE164}
                </a>
              )}
            </span>
            {editing && (
              <Badge tone={appointment!.status === 'NO_SHOW' ? 'bad' : 'neutral'}>
                {APPT_STATUS_ZH[appointment!.status] ?? appointment!.status}
              </Badge>
            )}
          </div>
        ) : (
          <WhoPicker value={picked} onChange={setPicked} />
        )}

        {/* ── 编辑模式的状态操作 ── */}
        {editing && (
          <Field label="标记状态">
            <div className="flex flex-wrap gap-2">
              {appointment!.status !== 'CONFIRMED' && appointment!.status !== 'ARRIVED' && (
                <Button
                  loading={setStatus.isPending}
                  onClick={() => setStatus.mutate('CONFIRMED')}
                >
                  已确认
                </Button>
              )}
              {appointment!.status !== 'ARRIVED' && (
                <Button
                  variant="primary"
                  loading={setStatus.isPending}
                  onClick={() => setStatus.mutate('ARRIVED')}
                >
                  已到店
                </Button>
              )}
              <Button loading={setStatus.isPending} onClick={() => setStatus.mutate('NO_SHOW')}>
                爽约没来
              </Button>
              <Button loading={setStatus.isPending} onClick={() => setStatus.mutate('CANCELLED')}>
                取消预约
              </Button>
            </div>
            <p className="mt-2 text-[15px] text-ink-3">
              「取消」会留一条记录在日历上；录错人想让它彻底消失，用下面的删除。
            </p>
          </Field>
        )}

        {/* ── 来做什么 ── */}
        <Field label="来做什么" hint="接电话时顺口问一句，日历上会直接显示">
          <Input
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="例：咨询做全口"
            maxLength={200}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {PURPOSES.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPurpose(p)}
                className={cx(
                  'h-10 rounded-full border px-3.5 text-[15px] font-semibold transition-colors',
                  purpose === p
                    ? 'border-brand-600 bg-brand-100 text-brand-700'
                    : 'border-line-strong bg-white text-ink-2 hover:bg-sunken',
                )}
              >
                {p}
              </button>
            ))}
          </div>
        </Field>

        {/* ── 什么时候 ── */}
        <Field label="日期" hint={day ? `${fmtDate(day)} ${weekday(`${day}T00:00:00Z`)}` : undefined}>
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="tnum" />
          <div className="mt-2 flex flex-wrap gap-2">
            {[
              ['今天', 0],
              ['明天', 1],
              ['+1周', 7],
              ['+2周', 14],
            ].map(([label, n]) => {
              const k = dateKey(addDays(new Date(), n as number))
              return (
                <button
                  key={label as string}
                  type="button"
                  onClick={() => setDay(k)}
                  className={cx(
                    'h-10 rounded-full border px-4 text-[15px] font-semibold transition-colors',
                    day === k
                      ? 'border-brand-600 bg-brand-100 text-brand-700'
                      : 'border-line-strong bg-white text-ink-2 hover:bg-sunken',
                  )}
                >
                  {label as string}
                </button>
              )
            })}
          </div>
        </Field>

        {restDay && (
          <p className="flex gap-2 rounded-md border border-warn-line bg-warn-bg px-4 py-3 text-[16px] text-warn">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            这天是{weekday(`${day}T00:00:00Z`)}，通常休息。确定要约就直接保存。
          </p>
        )}

        <Field label="时间">
          <Input type="time" value={hhmm} onChange={(e) => setHhmm(e.target.value)} className="tnum" />
        </Field>

        <Field label="时长">
          <Segmented
            value={String(duration)}
            onChange={(v) => setDuration(Number(v))}
            ariaLabel="时长"
            options={[
              { value: '15', label: '15 分' },
              { value: '30', label: '30 分' },
              { value: '45', label: '45 分' },
              { value: '60', label: '1 小时' },
            ]}
          />
        </Field>

        <Field label="备注">
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </Field>

        {editing && (
          <div className="border-t border-line pt-4">
            <Button
              variant="ghost"
              icon={<Trash2 size={18} />}
              className="text-ink-3 hover:text-bad"
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              删除这条预约
            </Button>
          </div>
        )}
      </div>
    </Drawer>
  )
}

/**
 * 选顾客：先搜老顾客，搜不到就直接填姓名电话。
 * 不强制建档 —— 接电话的三十秒问不完建档要的那些字段。
 */
function WhoPicker({
  value,
  onChange,
}: {
  value: Picked | null
  onChange: (v: Picked | null) => void
}) {
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [manual, setManual] = useState(false)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300)
    return () => clearTimeout(t)
  }, [q])

  const results = useQuery({
    queryKey: ['search', debounced],
    queryFn: () =>
      api.get<{ items: SearchItem[] }>(
        `/api/customers/search?q=${encodeURIComponent(debounced)}&limit=8`,
      ),
    // 空关键词返回最近顾客，省得每次都要先想起名字
    enabled: !manual,
    placeholderData: (p) => p,
  })

  const items = useMemo(() => results.data?.items ?? [], [results.data])
  const searching = debounced.trim().length >= 2

  // 已选定：显示结果，可撤回
  if (value) {
    return (
      <Field label="顾客">
        <div className="flex items-center gap-3 rounded-md border-2 border-brand-600 bg-brand-50 px-4 py-3.5">
          <Avatar name={value.name} size={40} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[18px] font-semibold">{value.name}</span>
            <span className="tnum block text-[15px] text-ink-2">
              {value.phone || '（未填电话）'}
              {!value.id && <span className="ml-2 text-ink-3">· 未建档</span>}
            </span>
          </span>
          <button
            type="button"
            onClick={() => {
              onChange(null)
              setManual(false)
              setQ('')
              setName('')
              setPhone('')
            }}
            aria-label="重新选择"
            className="flex size-10 shrink-0 items-center justify-center rounded-md text-ink-2 hover:bg-sunken"
          >
            <X size={20} />
          </button>
        </div>
      </Field>
    )
  }

  // 手动填姓名电话（陌生人来电）
  if (manual) {
    return (
      <div className="flex flex-col gap-4">
        <Field label="姓名" required hint="马来名整串填，不用拆">
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Siti binti Hassan"
          />
        </Field>
        <Field label="电话" hint="填 0 开头即可">
          <Input
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="0138908890"
          />
        </Field>
        <div className="flex gap-2">
          <Button
            variant="primary"
            disabled={!name.trim()}
            onClick={() => onChange({ id: null, name: name.trim(), phone: phone.trim() })}
          >
            用这个信息预约
          </Button>
          <Button onClick={() => setManual(false)}>返回搜索</Button>
        </div>
        <p className="text-[15px] text-ink-3">
          先不建档。等他到店了，在「今天」页点这条预约就能一键建档并开单。
        </p>
      </div>
    )
  }

  // 搜索老顾客
  return (
    <Field label="顾客" required>
      <div className="relative">
        <Search
          size={20}
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-3"
        />
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="电话后 4 位 / 姓名"
          className="pl-11"
        />
      </div>

      {searching && items.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1">
          {items.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => onChange({ id: c.id, name: c.fullName, phone: c.phoneMasked })}
                className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-left transition-colors hover:bg-sunken"
              >
                <Avatar name={c.fullName} size={40} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[17px] font-semibold">{c.fullName}</span>
                  <span className="tnum block text-[15px] text-ink-2">
                    {c.phoneMasked} · {c.caseCount} 张工单
                  </span>
                </span>
                {c.hasOutstanding && <Badge tone="warn">有欠款</Badge>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {searching && items.length === 0 && !results.isFetching && (
        <p className="mt-2 text-[16px] text-ink-2">找不到「{debounced}」</p>
      )}

      <Button
        className="mt-3"
        icon={<UserPlus size={20} />}
        onClick={() => {
          // 搜索框里输的多半就是姓名或电话，带过去省一次输入
          if (/^\d+$/.test(q.trim())) setPhone(q.trim())
          else setName(q.trim())
          setManual(true)
        }}
      >
        新顾客 / 电话预约
      </Button>
    </Field>
  )
}
